/**
 * Which uploaded source documents a generation can read, and how.
 *
 * The extraction model only takes PDFs and images (`@adornis/llm` refuses every other file part), so anything
 * else has to go through LibreOffice first. That is only safe for formats LibreOffice actually understands:
 * handed random bytes it does not fail, it imports them as text and returns a PDF of garbage for the model to
 * read from. Hence an allowlist rather than "convert whatever is left".
 *
 * Isomorphic on purpose. The upload picker, the mutation that starts a run and the worker that converts the
 * files all decide from this one list, so what the picker offers and what the server accepts cannot drift apart.
 */

const CONVERTIBLE_EXTENSIONS = [
  // Word, OpenDocument text, RTF
  'doc',
  'docx',
  'docm',
  'dotx',
  'odt',
  'rtf',
  // Excel, OpenDocument spreadsheets, CSV
  'xls',
  'xlsx',
  'xlsm',
  'ods',
  'csv',
  // PowerPoint, OpenDocument presentations
  'ppt',
  'pptx',
  'pps',
  'ppsx',
  'odp',
  // Plain text
  'txt',
  'md',
  'html',
  'htm',
];

/**
 * Matched as prefixes against the sniffed mimetype. `application/vnd.ms-office` is what `file` reports for an
 * old binary Office file (.doc/.xls/.ppt) it cannot tell apart.
 */
const CONVERTIBLE_MIMETYPES = [
  'application/msword',
  'application/rtf',
  'application/vnd.ms-office',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.',
  'application/vnd.oasis.opendocument.',
  'text/',
];

export type DocumentHandling = 'as-is' | 'convert';

/**
 * `as-is` for what the model reads directly, `convert` for what LibreOffice turns into a PDF first, `undefined`
 * for anything a generation cannot read.
 *
 * Both the sniffed mimetype and the extension count: the sniff can come back generic for a perfectly good file,
 * and the extension is only whatever the file happened to be named.
 */
export function documentHandling(
  meta?: { mimetype?: string | null; extension?: string | null } | null,
): DocumentHandling | undefined {
  const mimetype = meta?.mimetype ?? '';
  const extension = meta?.extension?.toLowerCase() ?? '';
  if (mimetype === 'application/pdf' || mimetype.startsWith('image/')) return 'as-is';
  if (CONVERTIBLE_EXTENSIONS.includes(extension) || CONVERTIBLE_MIMETYPES.some(type => mimetype.startsWith(type))) {
    return 'convert';
  }
  return undefined;
}

/** For the upload picker's `accept`. Dropped files ignore it, which is why the server checks again. */
export const DOCUMENT_ACCEPT = ['.pdf', 'image/*', ...CONVERTIBLE_EXTENSIONS.map(extension => `.${extension}`)].join(
  ',',
);
